import { jwtDecode } from 'jwt-decode';
import {
  AUTH_CODE,
  MFA_COOKIE,
  EMAIL,
  REDIRECT_APP,
  REDIRECT_PATH,
} from '@/constants/authentication';
import { RedirectApp } from '@/enums/RedirectApp';
import { ClientDomainType } from '@/generated/graphql';
import {
  INDIRECT_INV_KEY_LOCALE,
  INDIRECT_INV_KEY_PROJECT,
  INDIRECT_INV_KEY_PROJ_SUB_ID,
  INDIRECT_INV_KEY_STAGE,
  INDIRECT_INV_KEY_HIRING_MANAGERS,
} from '@/constants/indirectInvite';

const setMfaCookie = (mfaCookie: string) => {
  //Mfa cookie has to be stringified when saved
  localStorage.setItem(MFA_COOKIE, JSON.stringify(mfaCookie));
};

export const getAuthTokensFromLocalStorage = () => {
  return {
    authCode: localStorage.getItem(AUTH_CODE),
    mfaCookie: localStorage.getItem(MFA_COOKIE),
  };
};

export const setAuthTokensToLocalStorage = (
  authCode: string,
  mfaCookie?: string
) => {
  localStorage.setItem(AUTH_CODE, authCode);

  if (mfaCookie) {
    setMfaCookie(mfaCookie);
  }
};

export const setIndirectInvParamsToLocalStorage = ({
  projectId,
  stageId,
  locale,
  projectSubId,
  hms,
}: {
  projectId: string | null;
  stageId: string | null;
  locale: string | null;
  projectSubId: string | null;
  hms?: string | null;
}) => {
  localStorage.setItem(INDIRECT_INV_KEY_PROJECT, projectId || '');
  localStorage.setItem(INDIRECT_INV_KEY_STAGE, stageId || '');
  localStorage.setItem(INDIRECT_INV_KEY_LOCALE, locale || '');
  localStorage.setItem(INDIRECT_INV_KEY_PROJ_SUB_ID, projectSubId || '');
  localStorage.setItem(INDIRECT_INV_KEY_HIRING_MANAGERS, hms || '');
};

export const getIndirectInvParamsFromLocalStorage = () => {
  return {
    projectId: localStorage.getItem(INDIRECT_INV_KEY_PROJECT),
    stageId: localStorage.getItem(INDIRECT_INV_KEY_STAGE),
    locale: localStorage.getItem(INDIRECT_INV_KEY_LOCALE),
    projectSubId: localStorage.getItem(INDIRECT_INV_KEY_PROJ_SUB_ID),
    hms: localStorage.getItem(INDIRECT_INV_KEY_HIRING_MANAGERS),
  };
};

export const clearIndirectInvParamsFromLocalStorage = () => {
  localStorage.removeItem(INDIRECT_INV_KEY_PROJECT);
  localStorage.removeItem(INDIRECT_INV_KEY_STAGE);
  localStorage.removeItem(INDIRECT_INV_KEY_LOCALE);
  localStorage.removeItem(INDIRECT_INV_KEY_PROJ_SUB_ID);
  localStorage.removeItem(INDIRECT_INV_KEY_HIRING_MANAGERS);
};

export const clearAuthTokensFromLocalStorage = () => {
  localStorage.removeItem(AUTH_CODE);
};

export const saveRedirectInfoToLocalStorage = () => {
  const allQueryParams = new URLSearchParams(window.location.search);
  const redirectApp = allQueryParams.get(REDIRECT_APP);
  const redirectPath = allQueryParams.get(REDIRECT_PATH);

  if (redirectApp) {
    localStorage.setItem(REDIRECT_APP, redirectApp);

    if (redirectPath) {
      localStorage.setItem(REDIRECT_PATH, redirectPath);
    } else {
      localStorage.removeItem(REDIRECT_PATH);
    }

    localStorage.removeItem(EMAIL);

    if (!allQueryParams.get('indirect-inv')) {
      // Reset auth tokens as user was redirected from another page.
      // For indirect-inv we want to enable auto accept invite, so we don't want to
      // remove auth_tokens.
      clearAuthTokensFromLocalStorage();
    }
  }
};

export const getClientDomainType = (): ClientDomainType => {
  const redirectApp: RedirectApp | null = localStorage.getItem(
    REDIRECT_APP
  ) as RedirectApp;

  if (redirectApp === RedirectApp.ADMIN) {
    return ClientDomainType.AdminAppDomain;
  }

  if (redirectApp === RedirectApp.COMPANY) {
    return ClientDomainType.CompanyAppDomain;
  }

  return ClientDomainType.CandidateAppDomain;
};

// DIRECT (token parser)
type InvitationData = {
  email: string | undefined;
  givenName: string | undefined;
  familyName: string | undefined;
  locale: string | undefined;
  projectSubId: string | undefined;
};

type ParsedInvitationToken = {
  invitationData: {
    email: string;
    firstName: string;
    lastName: string;
    locale: string;
    projectSubId: string;
  };
} | null;

export function parseDirectInvitationToken(
  token: string | null
): InvitationData {
  if (token) {
    try {
      const parsedInvitation = jwtDecode<ParsedInvitationToken>(token);

      if (parsedInvitation) {
        const { email, firstName, lastName, locale, projectSubId } =
          parsedInvitation?.invitationData;

        return {
          email: email,
          givenName: firstName,
          familyName: lastName,
          locale: locale,
          projectSubId: projectSubId,
        };
      }
    } catch (error) {
      // Invalid token format
      return {
        email: undefined,
        givenName: undefined,
        familyName: undefined,
        locale: undefined,
        projectSubId: undefined,
      };
    }
  }

  return {
    email: undefined,
    givenName: undefined,
    familyName: undefined,
    locale: undefined,
    projectSubId: undefined,
  };
}
